/* Giveaway Switch-E (design v4): countdowns, auto-close, floating CTA, the entry form
   (sent straight to Klaviyo), confirmation, share, video sound, single-open FAQ and rules links.
   On a successful entry it fires `giveaway_entry_success` (dataLayer + Shopify custom pixels),
   with the email only as a SHA-256 hash for Meta Advanced Matching. */
(() => {
  console.log('[giveaway] script loaded (Klaviyo form, debug logging on)');
  const pad = (n) => String(n).padStart(2, '0');

  function initCountdown(root) {
    const closes = Date.parse(root.dataset.closes || '');
    if (Number.isNaN(closes)) return;

    const counters = root.querySelectorAll('[data-gw-countdown]');
    const entries = root.querySelectorAll('[data-gw-entry]');

    const close = () => {
      entries.forEach((entry) => {
        // Keep the confirmation visible for someone who just entered.
        if (entry.dataset.gwEntered === '1') return;
        entry.querySelector('[data-gw-closed]')?.removeAttribute('hidden');
        entry.querySelector('[data-gw-open]')?.setAttribute('hidden', '');
      });
      counters.forEach((counter) => {
        counter.querySelector('[data-gw-units]')?.setAttribute('hidden', '');
        counter.querySelector('[data-gw-closed-text]')?.removeAttribute('hidden');
      });
    };

    const tick = () => {
      const left = closes - Date.now();
      if (left <= 0) {
        close();
        clearInterval(root._gwTimer);
        return;
      }
      const s = Math.floor(left / 1000);
      const parts = { d: Math.floor(s / 86400), h: Math.floor(s / 3600) % 24, m: Math.floor(s / 60) % 60, s: s % 60 };
      counters.forEach((counter) => {
        counter.querySelectorAll('[data-gw-unit]').forEach((el) => {
          el.textContent = pad(parts[el.dataset.gwUnit]);
        });
      });
    };

    clearInterval(root._gwTimer);
    tick();
    root._gwTimer = setInterval(tick, 1000);
  }

  // Shows once the first hero has scrolled away; hides near the entry form and after entering.
  function initFloating(root) {
    const floats = root.querySelectorAll('[data-gw-float]');
    if (!floats.length) return;

    if (window.Shopify && window.Shopify.designMode) {
      floats.forEach((f) => f.classList.add('is-visible'));
      return;
    }

    const hero = root.querySelector('[data-gw-hero]');
    const entries = root.querySelectorAll('[data-gw-entry]');
    let entered = !!root.querySelector('[data-gw-entry][data-gw-entered]');
    const visible = new Set();

    const update = () => {
      const heroGone = hero ? !visible.has(hero) : window.scrollY > window.innerHeight * 0.6;
      const formShown = [...entries].some((e) => visible.has(e));
      floats.forEach((f) => f.classList.toggle('is-visible', heroGone && !formShown && !entered));
    };

    document.addEventListener('gw:entered', () => { entered = true; update(); });

    floats.forEach((f) => {
      try {
        if (sessionStorage.getItem(f.dataset.key) === '1') return f.remove();
      } catch (e) {}
      f.querySelector('[data-gw-float-close]')?.addEventListener('click', () => {
        try { sessionStorage.setItem(f.dataset.key, '1'); } catch (e) {}
        f.remove();
      });
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((items) => {
        items.forEach((item) => (item.isIntersecting ? visible.add(item.target) : visible.delete(item.target)));
        update();
      }, { threshold: 0.1 });
      if (hero) observer.observe(hero);
      entries.forEach((e) => observer.observe(e));
    } else {
      window.addEventListener('scroll', update, { passive: true });
    }
    update();
  }

  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const KLAVIYO_REVISION = '2026-01-15';
  const KLAVIYO_HEADERS = {
    'Content-Type': 'application/vnd.api+json',
    Accept: 'application/vnd.api+json',
    revision: KLAVIYO_REVISION,
  };

  const klaviyoUrl = (path, key) => `https://a.klaviyo.com/client/${path}?company_id=${encodeURIComponent(key)}`;

  function profileAttributes(entry, properties) {
    const attributes = { email: entry.email, properties };
    if (entry.firstName) attributes.first_name = entry.firstName;
    return attributes;
  }

  // Debug logging for the Klaviyo signup. Remove the console.* calls once it works.
  const log = (...args) => console.log('[giveaway]', ...args);

  // Analytics: tells GTM (dataLayer) and Shopify custom pixels that an entry succeeded.
  // The email is sent only as a SHA-256 hash (trimmed, lowercased), the format Meta expects.
  async function sha256(text) {
    const bytes = new TextEncoder().encode(text.trim().toLowerCase());
    const hash = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  async function trackEntrySuccess(entry, detail) {
    let emailHash;
    try { emailHash = entry.email ? await sha256(entry.email) : undefined; } catch (e) {}
    const data = {
      ...detail,
      email_sha256: emailHash,
      event_id: (crypto.randomUUID && crypto.randomUUID()) || `gw-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'giveaway_entry_success', ...data });
    try { window.Shopify?.analytics?.publish('giveaway_entry_success', data); } catch (e) {}
    log('tracked giveaway_entry_success', data);
  }

  // Sends one Klaviyo client API request and logs the request and the response.
  async function klaviyoPost(path, cfg, payload) {
    const url = klaviyoUrl(path, cfg.publicKey);
    log(`→ POST ${url}`, { headers: KLAVIYO_HEADERS, payload });
    let response;
    try {
      response = await fetch(url, { method: 'POST', headers: KLAVIYO_HEADERS, body: JSON.stringify(payload) });
    } catch (error) {
      // Network failure, CORS block, ad blocker or CSP: the request never got a response.
      console.error('[giveaway] ✗ request failed before Klaviyo answered (network, CORS, ad blocker or CSP?)', error);
      throw error;
    }
    const text = await response.text().catch(() => '');
    if (response.ok) log(`← ${response.status} ${path}`, text || '(no body)');
    else console.error(`[giveaway] ← ${response.status} ${path}`, text);
    return response;
  }

  // Create Client Subscription: adds the profile to the list (the list's opt-in setting
  // decides single or double opt-in). Success is 202 with no body.
  function klaviyoSubscribe(cfg, entry, properties) {
    return klaviyoPost('subscriptions', cfg, {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: cfg.source,
          profile: { data: { type: 'profile', attributes: profileAttributes(entry, properties) } },
        },
        relationships: { list: { data: { type: 'list', id: cfg.listId } } },
      },
    });
  }

  // Create or Update Client Profile: saves the properties without list membership or consent.
  function klaviyoProfile(cfg, entry, properties) {
    return klaviyoPost('profiles', cfg, { data: { type: 'profile', attributes: profileAttributes(entry, properties) } });
  }

  function readConfig(form) {
    const el = form.querySelector('script[data-gw-config]');
    if (!el) {
      console.error('[giveaway] no <script data-gw-config> found in the form');
      return {};
    }
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error('[giveaway] the form config is not valid JSON', error, el.textContent);
      return {};
    }
  }

  function initForms(root) {
    const forms = root.querySelectorAll('form[data-gw-form]');
    log(`found ${forms.length} giveaway form(s)`);
    forms.forEach((form) => {
      const cfg = readConfig(form);
      log('form config', { form: form.id, publicKey: cfg.publicKey || '(empty)', listId: cfg.listId || '(empty)', listMode: cfg.listMode, properties: cfg.properties });
      const errorBox = form.querySelector('[data-gw-error]');
      const name = form.querySelector('[data-gw-name]');
      const email = form.querySelector('[data-gw-email]');
      const country = form.querySelector('[data-gw-country]');
      const rules = form.querySelector('[data-gw-rules]');
      const newsletter = form.querySelector('[data-gw-newsletter]');
      const honeypot = form.querySelector('[data-gw-hp]');
      const button = form.querySelector('button[type="submit"]');
      const buttonLabel = button ? button.querySelector('.gw-cta__label') : null;
      const idleLabel = buttonLabel ? buttonLabel.textContent : '';
      let sending = false;

      const showError = (message, field) => {
        form.querySelectorAll('[aria-invalid]').forEach((el) => {
          el.removeAttribute('aria-invalid');
          el.removeAttribute('aria-describedby');
        });
        if (errorBox) errorBox.textContent = message || '';
        if (field && message) {
          field.setAttribute('aria-invalid', 'true');
          if (errorBox) field.setAttribute('aria-describedby', errorBox.id);
          field.focus();
        }
      };
      form.addEventListener('input', () => showError(''));
      form.addEventListener('change', () => showError(''));

      const setBusy = (busy) => {
        sending = busy;
        if (!button) return;
        button.disabled = busy;
        if (buttonLabel) buttonLabel.textContent = busy && cfg.loadingLabel ? cfg.loadingLabel : idleLabel;
      };

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        log('submit');
        if (sending) return log('already sending, ignored');

        if (email) email.value = email.value.trim();
        let invalid = null;
        if (name && !name.value.trim()) invalid = name;
        else if (email && !EMAIL.test(email.value)) invalid = email;
        else if (country && !country.value) invalid = country;
        else if (rules && rules.required && !rules.checked) invalid = rules;
        if (invalid) {
          log(`validation failed on "${invalid.name}", nothing sent`);
          return showError(invalid.dataset.error, invalid);
        }

        const entry = { firstName: name ? name.value.trim() : '', email: email.value };

        // Bots fill the honeypot: pretend it worked, send nothing.
        if (honeypot && honeypot.value) {
          log('honeypot filled (or autofilled by the browser), nothing sent. Value:', JSON.stringify(honeypot.value));
          return showSuccess(form.closest('[data-gw-entry]'), entry);
        }

        if (!cfg.publicKey || !cfg.listId) {
          console.warn('[giveaway] Klaviyo public API key or list ID is missing in the Entry form block settings. Nothing was sent.', { publicKey: cfg.publicKey, listId: cfg.listId });
          return showError(cfg.errorGeneric);
        }

        // Browser language (from the visitor's browser / OS settings), e.g. "nl-NL" -> "NL".
        // Helps reclassify people who read the page in the default language (EN).
        const browserLocale = (navigator.languages && navigator.languages[0]) || navigator.language || '';
        const properties = Object.assign({}, cfg.properties, {
          'Giveaway Signup URL': window.location.href,
          'Giveaway Browser Locale': browserLocale,
          'Giveaway Browser Language': browserLocale.split('-')[0].toUpperCase(),
        });
        if (country) properties['Giveaway Entry Country'] = country.value;
        if (newsletter) properties['Giveaway Newsletter Opt-in'] = newsletter.checked;
        Object.keys(properties).forEach((key) => {
          if (properties[key] === null || properties[key] === '') delete properties[key];
        });

        const addToList = cfg.listMode !== 'optin' || (newsletter && newsletter.checked);
        log(addToList ? 'sending: subscribe to list' : 'sending: profile only (opt-in mode, box not ticked)', { entry, properties });

        setBusy(true);
        try {
          const response = addToList ? await klaviyoSubscribe(cfg, entry, properties) : await klaviyoProfile(cfg, entry, properties);
          if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);
          if (addToList && cfg.profileFallback) {
            // Optional second call when subscribed profiles turn out to miss the properties.
            await klaviyoProfile(cfg, entry, properties).catch(() => {});
          }
        } catch (error) {
          setBusy(false);
          console.error('[giveaway] Klaviyo signup failed', error);
          return showError(cfg.errorGeneric);
        }
        log('signup done, showing confirmation');
        // Tracking never blocks the confirmation, even if it fails.
        trackEntrySuccess(entry, {
          form_id: form.id || undefined,
          list_mode: cfg.listMode,
          subscribed_to_list: addToList,
          newsletter_opt_in: newsletter ? newsletter.checked : undefined,
          entry_country: country ? country.value : undefined,
          browser_language: properties['Giveaway Browser Language'],
        }).catch(() => {});
        setBusy(false);
        showSuccess(form.closest('[data-gw-entry]'), entry);
      });
    });
  }

  function showSuccess(entryEl, data) {
    const success = entryEl ? entryEl.querySelector('[data-gw-success]') : null;
    if (!success) return;
    success.hidden = false;
    const fields = entryEl.querySelector('[data-gw-fields]');
    if (fields) fields.hidden = true;
    const intro = entryEl.querySelector('[data-gw-intro]');
    if (intro) intro.hidden = true;
    entryEl.dataset.gwEntered = '1';

    const suffix = success.querySelector('[data-gw-name-suffix]');
    if (suffix) suffix.textContent = data.firstName ? `, ${data.firstName}` : '';
    const text = success.querySelector('[data-gw-success-text]');
    if (text) {
      const walker = document.createTreeWalker(text, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.nodeValue.includes('[email]')) node.nodeValue = node.nodeValue.split('[email]').join(data.email || '');
      }
    }
    entryEl.scrollIntoView({ block: 'start' });
    success.focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('gw:entered'));
  }

  function initSuccess(root) {
    root.querySelectorAll('[data-gw-share]').forEach((share) => {
      share.addEventListener('click', async () => {
        const url = location.href.split('?')[0].split('#')[0];
        try {
          if (navigator.share) return await navigator.share({ url, title: document.title });
          await navigator.clipboard.writeText(url);
        } catch (e) { return; }
        const label = share.querySelector('.gw-cta__label');
        if (label && share.dataset.copied) label.textContent = share.dataset.copied;
      });
    });
  }

  function initVideo(root) {
    root.querySelectorAll('[data-gw-sound]').forEach((button) => {
      const video = button.closest('.gw-hero__media')?.querySelector('video');
      if (!video) return;
      button.addEventListener('click', () => {
        video.muted = !video.muted;
        if (video.paused) video.play().catch(() => {});
        button.textContent = video.muted ? button.dataset.on : button.dataset.off;
      });
    });
  }

  // Carousel: scroll-snap track with arrows, dots, optional auto-advance and video handling.
  function initCarousels(root) {
    root.querySelectorAll('[data-gw-carousel]').forEach((car) => {
      if (car.dataset.gwReady) return;
      car.dataset.gwReady = '1';
      const track = car.querySelector('[data-gw-car-track]');
      if (!track) return;
      const slides = Array.from(track.querySelectorAll(':scope > [data-gw-slide]'));
      const prev = car.querySelector('[data-gw-car-prev]');
      const next = car.querySelector('[data-gw-car-next]');
      const dotsBox = car.querySelector('[data-gw-car-dots]');
      const loop = car.dataset.loop === 'true';
      const delay = (parseInt(car.dataset.autoplay, 10) || 0) * 1000;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let current = 0;
      let timer = null;
      let hovering = false;
      let inView = false;

      if (slides.length < 2) car.setAttribute('data-single', '');

      const perView = () => {
        if (!slides[0]) return 1;
        return Math.max(1, Math.round(track.clientWidth / slides[0].getBoundingClientRect().width));
      };
      const lastIndex = () => Math.max(0, slides.length - perView());

      const dots = [];
      if (dotsBox) {
        slides.forEach((slide, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'gw-carousel__dot';
          dot.setAttribute('aria-label', `${dotsBox.dataset.label || 'Go to slide'} ${i + 1}`);
          dot.addEventListener('click', () => goTo(i));
          dotsBox.appendChild(dot);
          dots.push(dot);
        });
      }

      function goTo(index, smooth = true) {
        const max = lastIndex();
        if (index > max) index = loop ? 0 : max;
        if (index < 0) index = loop ? max : 0;
        const slide = slides[index];
        if (!slide) return;
        track.scrollTo({ left: slide.offsetLeft, behavior: smooth && !reduced ? 'smooth' : 'auto' });
      }

      function pauseMedia(slide) {
        slide.querySelectorAll('video').forEach((v) => v.pause());
        slide.querySelectorAll('iframe[data-gw-slide-embed]').forEach((f) => {
          const msg = f.dataset.gwSlideEmbed === 'youtube'
            ? '{"event":"command","func":"pauseVideo","args":""}'
            : '{"method":"pause"}';
          try { f.contentWindow.postMessage(msg, '*'); } catch (e) { /* ignore */ }
        });
      }

      // YouTube / Vimeo load when their slide is first shown; afterwards they are paused / resumed.
      function loadEmbeds(slide) {
        slide.querySelectorAll('iframe[data-src]').forEach((f) => {
          f.src = f.dataset.src;
          f.removeAttribute('data-src');
          f.dataset.gwFresh = '1';
        });
      }
      function playMedia(slide) {
        slide.querySelectorAll('video').forEach((v) => { if (v.paused) v.play().catch(() => {}); });
        slide.querySelectorAll('iframe[data-gw-slide-embed]').forEach((f) => {
          if (f.dataset.gwFresh) { delete f.dataset.gwFresh; return; } // just loaded: its autoplay URL starts it
          const msg = f.dataset.gwSlideEmbed === 'youtube'
            ? '{"event":"command","func":"playVideo","args":""}'
            : '{"method":"play"}';
          try { f.contentWindow.postMessage(msg, '*'); } catch (e) { /* ignore */ }
        });
      }

      function update() {
        const left = track.scrollLeft;
        let best = 0;
        let bestDist = Infinity;
        slides.forEach((slide, i) => {
          const d = Math.abs(slide.offsetLeft - left);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) best = Math.max(best, lastIndex());
        current = best;
        const visibleTo = current + perView() - 1;
        slides.forEach((slide, i) => {
          const visible = i >= current && i <= visibleTo;
          slide.toggleAttribute('data-active', visible);
          slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
          if (!visible) pauseMedia(slide);
          else if (inView) {
            loadEmbeds(slide);
            if (slide.hasAttribute('data-autoplay')) playMedia(slide);
          }
        });
        dots.forEach((dot, i) => dot.setAttribute('aria-current', i === current ? 'true' : 'false'));
        if (prev) prev.disabled = !loop && current <= 0;
        if (next) next.disabled = !loop && current >= lastIndex();
      }

      let raf = 0;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(update);
      }, { passive: true });
      prev?.addEventListener('click', () => { goTo(current - 1); restart(); });
      next?.addEventListener('click', () => { goTo(current + 1); restart(); });
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
      });
      window.addEventListener('resize', () => update());

      // A video playing with sound holds the carousel still.
      const soundPlaying = () => slides.some((s) => Array.from(s.querySelectorAll('video')).some((v) => !v.paused && !v.muted));
      function tick() {
        if (hovering || !inView || document.hidden || soundPlaying()) return;
        goTo(current + 1);
      }
      function restart() {
        if (timer) clearInterval(timer);
        timer = delay && !reduced && slides.length > 1 ? setInterval(tick, delay) : null;
      }
      car.addEventListener('mouseenter', () => { hovering = true; });
      car.addEventListener('mouseleave', () => { hovering = false; });
      car.addEventListener('focusin', () => { hovering = true; });
      car.addEventListener('focusout', () => { hovering = false; });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver((items) => {
          inView = items[0].isIntersecting;
          if (!inView) slides.forEach(pauseMedia);
          update();
        }, { threshold: 0.4 }).observe(car);
      } else {
        inView = true;
      }

      // Theme editor: selecting a slide block scrolls to it.
      document.addEventListener('shopify:block:select', (event) => {
        const i = slides.indexOf(event.target.closest?.('[data-gw-slide]'));
        if (i > -1) goTo(i, false);
      });

      update();
      restart();
    });
  }

  // Plaque buttons: shrink a long label (e.g. French) until it fits the plaque, wrap only as a last resort.
  const PLAQUE_MIN_FONT = 15;
  function fitPlaqueLabel(label) {
    label.style.fontSize = '';
    label.removeAttribute('data-wrap');
    const base = parseFloat(getComputedStyle(label).fontSize) || 24;
    const style = getComputedStyle(label);
    const room = label.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    if (room <= 0) return;
    let size = base;
    const measure = () => {
      const r = document.createRange();
      r.selectNodeContents(label);
      return r.getBoundingClientRect().width;
    };
    while (measure() > room && size > PLAQUE_MIN_FONT) {
      size -= 1;
      label.style.fontSize = `${size}px`;
    }
    if (measure() > room) label.setAttribute('data-wrap', '');
  }
  function initPlaques(root) {
    const labels = root.querySelectorAll('.gw-cta--plaque .gw-cta__label, .gw-submit--art .gw-cta__label');
    if (!labels.length) return;
    const fitAll = () => labels.forEach(fitPlaqueLabel);
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => fitAll());
      labels.forEach((label) => ro.observe(label.parentElement));
    } else {
      window.addEventListener('resize', fitAll);
    }
    // Re-fit when the text changes (e.g. "Sending…") and once the display font has loaded.
    labels.forEach((label) => new MutationObserver(() => fitPlaqueLabel(label)).observe(label, { childList: true, characterData: true, subtree: true }));
    document.fonts?.ready.then(fitAll);
    fitAll();
  }

  function initFaq(root) {
    root.querySelectorAll('.gw-faq__list[data-single="true"]').forEach((list) => {
      list.addEventListener('toggle', (event) => {
        const opened = event.target;
        if (!opened.matches('[data-gw-acc]') || !opened.open) return;
        list.querySelectorAll('[data-gw-acc][open]').forEach((other) => { if (other !== opened) other.open = false; });
      }, true);
    });
  }

  // Links to the rules anchor open the folded rules card.
  function openRulesFor(hash) {
    if (!hash || hash.length < 2) return;
    let target;
    try { target = document.querySelector(hash); } catch (e) { return; }
    const card = target?.matches('[data-gw-rules-toggle]') ? target : target?.querySelector('[data-gw-rules-toggle]');
    if (card) card.open = true;
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-gw]').forEach((root) => {
      initSuccess(root);
      initCountdown(root);
      initFloating(root);
      initForms(root);
      initVideo(root);
      initCarousels(root);
      initPlaques(root);
      initFaq(root);
    });
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (link && link.closest('[data-gw]')) openRulesFor(link.getAttribute('href'));
  });
  window.addEventListener('hashchange', () => openRulesFor(location.hash));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init(); openRulesFor(location.hash); });
  } else {
    init();
    openRulesFor(location.hash);
  }

  // Theme editor: re-run when the section is re-rendered.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-gw]').forEach((root) => clearInterval(root._gwTimer));
  });
})();